<!--
  The pill. The wrapper is the popover's trigger (data-pop-trigger, so the band's capture-phase click-away never
  closes the card on the press that opens it) and the overlay origin the card is measured from. The control is a
  Paper button with its own content: the mark, the activity dots, the title, the time and the heads.
-->
@if (shown()) {
  @let live = session();
  <span
    class="pill"
    cdkOverlayOrigin
    #pillOrigin="cdkOverlayOrigin"
    data-pop-trigger="1"
    data-testid="flow-session-indicator"
    [attr.data-recording]="recording()"
    [class.is-open]="open()"
    (mouseenter)="onPointerEnter()"
    (mouseleave)="onPointerLeave()">
    <paper-button
      class="pill__button"
      variant="ghost"
      shape="pill"
      size="sm"
      buttonId="flow-session-indicator-button"
      [ariaHaspopup]="'dialog'"
      [ariaExpanded]="open()"
      [ariaControls]="cardId"
      (click)="onPillClick()">
      <span class="mark" aria-hidden="true"><im-flow-mark [size]="18" state="rest" colour="identity" /></span>
      <span class="dots" [class.is-moving]="moving()" aria-hidden="true"><i></i><i></i><i></i></span>
      <span class="sr-only">{{ 'shell.flow_session.live_session' | transloco }}:</span>
      <span class="title ph-mask" data-testid="flow-session-indicator-title">{{ live?.title || ('shell.flow_session.untitled' | transloco) }}</span>
      @if (time()) {
        <span class="time" data-testid="flow-session-indicator-time">{{ time() }}</span>
      }
      @if (pillHeads().length) {
        <span class="heads" aria-hidden="true" data-testid="flow-session-indicator-heads">
          @for (head of pillHeads(); track head.employeeId) {
            <span class="head" [attr.data-where]="head.where">
              <paper-avatar [initials]="head.initials" [image]="head.avatarUrl ?? undefined" [size]="24" />
            </span>
          }
        </span>
      }
    </paper-button>
  </span>

  <!--
    PORTALLED OUT, as the alert beacon's popover is: the bar is glass, and a backdrop-filter element is the
    containing block for anything fixed inside it. The CDK pane lives on body; anchorFrom measures the box and
    data-popover supplies the surface (styles.scss), so the card follows the popover rules.
  -->
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="pillOrigin"
    [cdkConnectedOverlayOpen]="open()"
    cdkConnectedOverlayPanelClass="flow-session-card-portal"
    (detach)="closeCard({ restoreFocus: false })">
    <section
      class="card ph-no-capture"
      role="dialog"
      [id]="cardId"
      [attr.data-popover]="cardKey"
      data-testid="flow-session-card"
      [attr.aria-label]="'shell.flow_session.live_session' | transloco"
      [attr.data-recording]="recording()"
      [style.top.px]="box()?.top"
      [style.left.px]="box()?.left"
      [style.width.px]="box()?.width"
      [style.max-height]="box()?.maxHeightCss"
      (mouseenter)="onPointerEnter()"
      (mouseleave)="onPointerLeave()">
      @if (!confirming()) {
        <header class="card__head">
          <h2 class="card__title ph-mask" data-testid="flow-session-card-title">{{ live?.title || ('shell.flow_session.untitled' | transloco) }}</h2>
          <span class="chip" data-testid="flow-session-card-state" [attr.data-tone]="chip().tone">
            @if (chip().tone === 'rec') { <span class="chip__dot" aria-hidden="true"></span> }
            {{ chip().key | transloco }}@if (chip().withTime && time()) { <span class="chip__time">{{ time() }}</span> }
          </span>
        </header>

        <ul class="people" data-testid="flow-session-card-heads" [attr.aria-label]="'shell.flow_session.heads_label' | transloco">
          @for (head of cardHeads().shown; track head.employeeId) {
            <li class="person" data-testid="flow-session-card-head" [attr.data-where]="head.where" [attr.data-me]="head.me">
              <paper-avatar [initials]="head.initials" [image]="head.avatarUrl ?? undefined" [size]="32" />
              <span class="person__text">
                <span class="person__name">{{ head.me ? ('shell.flow_session.you' | transloco) : head.name }}</span>
                <span class="person__where">{{ whereKey(head.where) | transloco }}</span>
              </span>
            </li>
          }
          @if (cardHeads().more) {
            <li class="person person--more" aria-hidden="true">+{{ cardHeads().more }}</li>
          }
        </ul>

        @if (lines().length) {
          <div class="lines" data-testid="flow-session-card-lines" [attr.aria-label]="'shell.flow_session.lines_label' | transloco" role="group">
            @for (line of lines(); track line.id) {
              <p class="line ph-mask">
                @if (line.speaker) { <span class="line__speaker">{{ line.speaker }}</span> }
                <span class="line__text">{{ line.text }}</span>
              </p>
            }
          </div>
        }

        <footer class="actions">
          <paper-button
            class="action"
            variant="outline"
            shape="pill"
            size="sm"
            icon="arrow-right"
            buttonId="flow-session-card-back"
            data-testid="flow-session-card-back"
            (click)="back()">{{ 'shell.flow_session.back' | transloco }}</paper-button>
          @if (live?.boardId) {
            <paper-button
              class="action"
              variant="outline"
              shape="pill"
              size="sm"
              icon="layout-grid"
              buttonId="flow-session-card-board"
              data-testid="flow-session-card-board"
              (click)="openBoard()">{{ 'shell.flow_session.board' | transloco }}</paper-button>
          }
          @if (canMute()) {
            <paper-button
              class="action action--icon"
              variant="ghost"
              shape="icon"
              size="sm"
              [icon]="paused() ? 'play' : 'pause'"
              buttonId="flow-session-card-mute"
              data-testid="flow-session-card-mute"
              [ariaPressed]="paused()"
              [ariaLabel]="(paused() ? 'shell.flow_session.resume' : 'shell.flow_session.pause') | transloco"
              [title]="(paused() ? 'shell.flow_session.resume' : 'shell.flow_session.pause') | transloco"
              (click)="togglePause()" />
          }
          <span class="actions__spacer"></span>
          @if (live?.canEnd) {
            <paper-button
              class="action"
              intent="destructive"
              shape="pill"
              size="sm"
              buttonId="flow-session-card-end"
              data-testid="flow-session-card-end"
              (click)="askEnd()">{{ 'shell.flow_session.end' | transloco }}</paper-button>
          } @else {
            <paper-button
              class="action"
              variant="ghost"
              shape="pill"
              size="sm"
              icon="log-out"
              buttonId="flow-session-leave"
              data-testid="flow-session-leave"
              (click)="leave()">{{ 'shell.flow_session.leave' | transloco }}</paper-button>
          }
        </footer>
      } @else {
        <!-- End, once (DIRECTION 2.1 step 8): what it does, then End for everyone or Leave. -->
        <div class="confirm" role="group" data-testid="flow-session-end-confirm" [attr.aria-labelledby]="confirmTitleId">
          <h2 class="confirm__title" [id]="confirmTitleId">{{ 'shell.flow_session.end_title' | transloco }}</h2>
          <ul class="confirm__what">
            <!-- End for everyone stops and saves every recording of the session, on any device (flow-recording-recovery). -->
            @if (recordsLive()) { <li>{{ 'shell.flow_session.end_recording' | transloco }}</li> }
            @if (live?.boardId) { <li>{{ 'shell.flow_session.end_board' | transloco }}</li> }
            <li>{{ 'shell.flow_session.end_everyone_leaves' | transloco }}</li>
            @if (live?.projectId) { <li>{{ 'shell.flow_session.end_project' | transloco }}</li> }
          </ul>
          @if (endFailed()) {
            <p class="confirm__trouble" role="alert" data-testid="flow-session-end-failed">{{ 'shell.flow_session.end_failed' | transloco }}</p>
          }
          <footer class="actions">
            <paper-button
              class="action"
              intent="destructive"
              shape="pill"
              size="sm"
              buttonId="flow-session-end-everyone"
              data-testid="flow-session-end-everyone"
              [loading]="ending()"
              [disabled]="ending()"
              (click)="endForEveryone()">{{ 'shell.flow_session.end_everyone' | transloco }}</paper-button>
            <paper-button
              class="action"
              variant="outline"
              shape="pill"
              size="sm"
              icon="log-out"
              buttonId="flow-session-leave"
              data-testid="flow-session-leave"
              [disabled]="ending()"
              (click)="leave()">{{ 'shell.flow_session.leave' | transloco }}</paper-button>
          </footer>
        </div>
      }
    </section>
  </ng-template>
}
